// 2026-09-24 - FormuLearning - Step 7 Final Report API 호출 (이슈 #59)
// 파일 위치: frontend/src/api/report.ts
//
// 제형연구보고서 — 절 단위 누적(각 Step Save and continue 때 반영) · 재생성 · 표지 입력 · docx 다운로드.
// 설계: ai-agent/claude/260924_#59_Step7_FinalReport/설계_260924_Step7 Final Report v1.0.md §5

import client from './client'
import type { ApiResponse } from '../types/api'

export type ReportBlock =
  | { type: 'heading'; level: number; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'bullets'; items: string[] }
  | { type: 'table'; columns: string[]; rows: string[][]; caption?: string | null }
  | { type: 'summary'; label: string; ko: string | null; en: string | null }
  | { type: 'note'; text: string }

export type SectionStatus = 'fresh' | 'stale' | 'empty' | 'static'

export interface ReportSection {
  code: string
  number: string
  title: string
  step: number | null
  kind: 'data' | 'narrative' | 'static'
  chapter: string
  self_headed: boolean
  status: SectionStatus
  generated_at: string | null
  blocks: ReportBlock[]
}

export interface ReportMeta {
  product: string
  subtitle: string
  report_date: string
  organization: string
  approvals: { role: string; name: string; position: string }[]
}

export interface ReportView {
  sections: ReportSection[]
  meta: ReportMeta
  is_final: boolean
  missing_steps: number[]
  last_download: { version: number; generated_at: string | null } | null
}

export async function getReport(projectId: string): Promise<ReportView> {
  const { data } = await client.get<ApiResponse<ReportView>>(`/report/${projectId}/`)
  return data.data!
}

/** 각 Step Save and continue 직후 그 Step 절을 반영한다. 실패는 저장 흐름을 막지 않게 호출부가 삼킨다. */
export async function refreshReportStep(projectId: string, step: number): Promise<void> {
  await client.post(`/report/${projectId}/sections/refresh`, { step })
}

export async function regenerateReport(projectId: string, mode: 'stale' | 'all'): Promise<ReportView> {
  const { data } = await client.post<ApiResponse<ReportView>>(`/report/${projectId}/regenerate`, { mode })
  return data.data!
}

export async function saveReportMeta(projectId: string, meta: ReportMeta): Promise<ReportMeta> {
  const { data } = await client.put<ApiResponse<ReportMeta>>(`/report/${projectId}/meta`, meta)
  return data.data!
}

export async function downloadReport(projectId: string): Promise<void> {
  const res = await client.get(`/report/${projectId}/download`, { responseType: 'blob' })
  const cd = res.headers['content-disposition'] ?? ''
  const star = /filename\*=UTF-8''([^;]+)/.exec(cd)?.[1]
  const name = star ? decodeURIComponent(star) : '제형연구보고서.docx'
  const url = URL.createObjectURL(res.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  URL.revokeObjectURL(url)
}
